import { useState, useRef, useEffect } from 'react';
import Icon from '../Icon/Icon';
import styles from './DatePicker.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '../../../types/types';
import { createPortal } from 'react-dom';
import ErrorMessage from '../../../utils/fieldErrorMesssage';

// Helper function to get the number of days in a month
const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

// Helper function to get the first day of the month (0=Sun, 1=Mon, ...)
const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

interface DatePickerProps {
  selectedDate?: Date;
  setSelectedDate: (date: Date) => void;
  size?: 'sm' | 'md';
  placeholder?: string;
  invalid?: boolean;
  errorMsg?: string;
  disablePastDate?: boolean;
  disableFutureDate?: boolean;
}

const DatePicker = ({
  selectedDate,
  setSelectedDate,
  size,
  placeholder = 'Date',
  invalid,
  errorMsg,
  disablePastDate,
  disableFutureDate,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const [calendarPosition, setCalendarPosition] = useState({
    top: 0,
    left: 0,
  });

  const datepickerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datepickerRef.current && !datepickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      if (datepickerRef.current) {
        const rect = datepickerRef.current.getBoundingClientRect();
        setCalendarPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }

      setViewDate(selectedDate || new Date());
    }
    setIsOpen(!isOpen);
  };

  const isPastDate = (dateStr: Date) => {
    const inputDate = new Date(dateStr);
    const today = new Date();
    return inputDate < today;
  };

  const isFutureDate = (dateStr: Date): boolean => {
    if (!(dateStr instanceof Date) || isNaN(dateStr.getTime())) return true; // Invalid Date

    const today = new Date();
    dateStr.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return dateStr > today;
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(year, month, day);

    if (disablePastDate && isPastDate(newDate)) return;
    if (disableFutureDate && isFutureDate(newDate)) return;

    setSelectedDate(newDate);
    setIsOpen(false);
  };

  const handlePrev = () => {
    setViewDate(new Date(year, month - 1, 1));
  };

  const handleNext = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const getHeaderText = () => {
    return `${viewDate.toLocaleString('default', { month: 'long' })} ${year}`;
  };

  const generateNext12Months = () => {
    const months = [];
    const currentDate = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      months.push({
        display: `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`,
        value: date,
      });
    }

    return months;
  };

  const handleMonthSelect = (selectedDate: Date) => {
    setViewDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    setIsDropdownOpen(false);
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const renderDaysGrid = () => {
    const days = [];
    const totalDays = daysInMonth(year, month);
    const startDay = firstDayOfMonth(year, month);
    const today = new Date();
    const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    dayLabels.forEach((label) => {
      days.push(
        <div key={label} className={`day_label ${styles.day_label}`}>
          {label}
        </div>
      );
    });

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`blank_${i}`} className={`${styles.blank_day}`}></div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const currentDate = new Date(year, month, day);
      const isSelected = selectedDate && currentDate.toDateString() === selectedDate.toDateString();
      const isToday = currentDate.toDateString() === today.toDateString();

      let classNames = `${styles.day}`;
      if (isSelected) classNames += ` ${styles.day_selected}`;
      else if (isToday && !selectedDate) classNames += ` ${styles.current_day}`;
      if (
        (disablePastDate && isPastDate(currentDate)) ||
        (disableFutureDate && isFutureDate(currentDate))
      )
        classNames += ` ${styles.past_future_date} ${styles.no_hover}`;

      days.push(
        <div key={day} className={classNames} onClick={() => handleDateClick(day)}>
          {day}
        </div>
      );
    }
    return <div className={`${styles.days_grid}`}>{days}</div>;
  };

  const calendarEl = isOpen && (
    <div
      className={`${styles.calendar}`}
      style={{
        top: `${calendarPosition.top}px`,
        left: `${calendarPosition.left}px`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={`${styles.calendar_header}`}>
        <div className={`${styles.month_dropdown_container}`}>
          <button className={`${styles.header_button}`} onClick={toggleDropdown}>
            {getHeaderText()}
            <Icon
              name="caret_down"
              size={14}
              className={`${styles.dropdown_icon} ${isDropdownOpen ? styles.rotate : ''}`}
              color={currentTheme === 'light' ? '#5969D6' : '#99B2F5'}
            />
          </button>
          {isDropdownOpen && (
            <div className={`${styles.month_dropdown_overlay}`}>
              <div className={`${styles.month_dropdown}`} ref={dropdownRef}>
                {generateNext12Months().map((month, index) => (
                  <button
                    key={index}
                    className={`${styles.month_option}`}
                    onClick={() => handleMonthSelect(month.value)}
                  >
                    {month.display}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <button onClick={handlePrev} className={`${styles.icon_button} ${styles.left}`}>
            <Icon
              name="arrow_left"
              size={14}
              color={currentTheme === 'light' ? '#707070' : '#D8D8D9'}
            />
          </button>
          <button onClick={handleNext} className={`${styles.icon_button} ${styles.right}`}>
            <Icon
              name="arrow_right"
              size={14}
              color={currentTheme === 'light' ? '#707070' : '#D8D8D9'}
            />
          </button>
        </div>
      </div>
      {renderDaysGrid()}
    </div>
  );

  return (
    <div className={`${styles.datepicker}`} ref={datepickerRef}>
      <div className={`${styles.input_wrapper}`} onClick={toggleOpen}>
        <input
          className={`${styles.datepicker_input} 
                ${isOpen ? styles.selected_input : ''} 
                ${selectedDate ? styles.input_with_text : ''} 
                ${styles[`size_${size}`]} ${invalid ? styles.invalid : ''}`}
          readOnly
          placeholder={placeholder}
          value={selectedDate ? selectedDate.toLocaleDateString() : ''}
        />
        <Icon
          name="calendar"
          className={`${styles.calendar_icon}`}
          color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
        />
        {size !== 'sm' && selectedDate && (
          <label className={`${styles.datepicker_placeholder}`}>{placeholder}</label>
        )}
      </div>
      <ErrorMessage message={errorMsg} className={styles.error_message} />

      {createPortal(calendarEl, document.getElementById('overlay-portal')!)}
    </div>
  );
};

export default DatePicker;
